"use client";
// Modal to create or edit one entry with its fields and links. A new entry starts linked to whatever
// was selected (new criterion under the selected requirement, new feature covering it, and so on).
import { createContext, useContext, useEffect, useState, useTransition } from "react";
import {
  deleteEntry,
  saveCriterion,
  saveFeature,
  saveGoal,
  saveMeasure,
  saveQuestion,
  saveRequirement,
} from "./actions.ts";
import type { Board, Kind } from "@/lib/types.ts";
import { key, parseKey, type Graph, type Key } from "./graph.ts";

export interface EditTarget {
  kind: Kind;
  id: number | null;
  /** Selected entry when "+ Neu" was clicked. */
  context?: Key | null;
}

const KIND_NAME: Record<Kind, string> = {
  goal: "Ziel",
  measure: "Messgröße",
  requirement: "Anforderung",
  criterion: "Abnahmekriterium",
  feature: "Feature",
  question: "Frage",
};

interface Form {
  title: string;
  description: string;
  goalId: number | null;
  priority: string;
  /** Requirement: its Bereich (main requirement); null = this is a Bereich itself. */
  parentId: number | null;
  status: string;
  requirementId: number | null;
  goalIds: number[];
  requirementIds: number[];
  featureIds: number[];
  bausteine: { id?: number; title: string }[];
  topic: string;
  answer: string;
  important: boolean;
  links: Key[];
  questionIds: number[];
  /** Questions typed in this editor; created and linked when the entry is saved. */
  newQuestions: string[];
}

function initialForm(board: Board, graph: Graph, t: EditTarget): Form {
  const f: Form = {
    title: "",
    description: "",
    goalId: null,
    priority: "Muss",
    parentId: null,
    status: t.kind === "feature" ? "geplant" : t.kind === "goal" || t.kind === "measure" ? "Entwurf" : "offen",
    requirementId: null,
    goalIds: [],
    requirementIds: [],
    featureIds: [],
    bausteine: [],
    topic: "",
    answer: "",
    important: false,
    links: [],
    questionIds: [],
    newQuestions: [],
  };
  if (t.id === null) {
    const [ck, cid] = t.context ? parseKey(t.context) : [null, null];
    if (t.kind === "measure" && ck === "goal") f.goalId = cid;
    if (t.kind === "measure" && ck === "measure")
      f.goalId = board.measures.find((m) => m.id === cid)?.goalId ?? null;
    if (t.kind === "criterion" && ck === "requirement") f.requirementId = cid;
    if (t.kind === "criterion" && ck === "criterion")
      f.requirementId = board.criteria.find((c) => c.id === cid)?.requirementId ?? null;
    if ((t.kind === "feature" || t.kind === "goal") && ck === "requirement") f.requirementIds = [cid!];
    if (t.kind === "requirement" && ck === "goal") f.goalIds = [cid!];
    if (t.kind === "requirement" && ck === "requirement")
      f.parentId = board.requirements.find((r) => r.id === cid)?.parentId ?? cid;
    if (t.kind === "requirement" && ck === "feature") f.featureIds = [cid!];
    if (t.kind === "question" && t.context) f.links = [t.context];
    if (t.kind !== "question" && ck === "question") f.questionIds = [cid!];
    if (t.kind === "question" && ck === "question")
      f.topic = board.questions.find((q) => q.id === cid)?.topic ?? "";
    return f;
  }
  const id = t.id;
  f.questionIds = graph.questionsOf(key(t.kind, id));
  if (t.kind === "goal") {
    const g = board.goals.find((e) => e.id === id)!;
    Object.assign(f, { title: g.title, description: g.description ?? "", status: g.status });
    f.requirementIds = graph.reqsOfGoal.get(id) ?? [];
  } else if (t.kind === "measure") {
    const m = board.measures.find((e) => e.id === id)!;
    Object.assign(f, { title: m.title, description: m.description ?? "", goalId: m.goalId, status: m.status });
  } else if (t.kind === "requirement") {
    const r = board.requirements.find((e) => e.id === id)!;
    Object.assign(f, { title: r.title, description: r.description ?? "", priority: r.priority, parentId: r.parentId });
    f.goalIds = graph.goalsOfReq.get(id) ?? [];
    f.featureIds = graph.featuresOfReq.get(id) ?? [];
  } else if (t.kind === "criterion") {
    const c = board.criteria.find((e) => e.id === id)!;
    Object.assign(f, { title: c.title, description: c.description ?? "", status: c.status, requirementId: c.requirementId });
  } else if (t.kind === "feature") {
    const x = board.features.find((e) => e.id === id)!;
    Object.assign(f, { title: x.title, description: x.description ?? "", status: x.status });
    f.requirementIds = graph.reqsOfFeature.get(id) ?? [];
    f.bausteine = board.bausteine.filter((b) => b.featureId === id).map((b) => ({ id: b.id, title: b.title }));
  } else {
    const q = board.questions.find((e) => e.id === id)!;
    Object.assign(f, {
      title: q.title,
      description: q.description ?? "",
      status: q.status,
      topic: q.topic ?? "",
      answer: q.answer ?? "",
      important: q.important,
    });
    f.links = [...graph.related(key("question", id))];
  }
  return f;
}

export function Editor({
  board,
  graph,
  target,
  onClose,
  onSaved,
  onDeleted,
  onOpen,
}: {
  board: Board;
  graph: Graph;
  target: EditTarget;
  onClose: () => void;
  onSaved: (kind: Kind, id: number) => void;
  onDeleted: () => void;
  /** Open another entry (a linked one clicked in a list); asks first if this form has unsaved changes. */
  onOpen: (kind: Kind, id: number) => void;
}) {
  const [initial] = useState<Form>(() => initialForm(board, graph, target));
  const [form, setForm] = useState<Form>(initial);
  const openOther = (k: Kind, id: number) => {
    if (JSON.stringify(form) !== JSON.stringify(initial) && !confirm("Ungespeicherte Änderungen verwerfen?")) return;
    onOpen(k, id);
  };
  const [error, setError] = useState<string | null>(null);
  const [pending, start] = useTransition();
  const set = <K extends keyof Form>(k: K, v: Form[K]) => setForm((f) => ({ ...f, [k]: v }));
  const { kind } = target;
  const projectId = board.project.id;

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    start(async () => {
      try {
        const id = target.id ?? undefined;
        let saved: number;
        if (kind === "goal")
          saved = await saveGoal({ id, projectId, title: form.title, status: form.status, description: form.description, requirementIds: form.requirementIds, questionIds: form.questionIds });
        else if (kind === "measure")
          saved = await saveMeasure({ id, projectId, title: form.title, status: form.status, goalId: form.goalId ?? NaN, description: form.description, questionIds: form.questionIds });
        else if (kind === "requirement")
          saved = await saveRequirement({ id, projectId, title: form.title, priority: form.priority, parentId: form.parentId, description: form.description, goalIds: form.goalIds, featureIds: form.featureIds, questionIds: form.questionIds });
        else if (kind === "criterion")
          saved = await saveCriterion({ id, projectId, title: form.title, requirementId: form.requirementId ?? NaN, status: form.status, description: form.description, questionIds: form.questionIds });
        else if (kind === "feature")
          saved = await saveFeature({ id, projectId, title: form.title, status: form.status, description: form.description, requirementIds: form.requirementIds, bausteine: form.bausteine, questionIds: form.questionIds });
        else
          saved = await saveQuestion({
            id,
            projectId,
            title: form.title,
            topic: form.topic,
            answer: form.answer,
            status: form.status,
            important: form.important,
            description: form.description,
            links: form.links.map((k) => {
              const [kind, targetId] = parseKey(k);
              return { kind, targetId };
            }),
          });
        for (const title of form.newQuestions)
          await saveQuestion({ projectId, title, status: "offen", important: false, links: [{ kind, targetId: saved }] });
        onSaved(kind, saved);
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
      }
    });
  };

  const remove = () => {
    if (target.id === null) return;
    const extra =
      kind === "requirement" ? " Seine Abnahmekriterien werden mitgelöscht." : kind === "feature" ? " Seine Bausteine werden mitgelöscht." : "";
    if (!confirm(`${KIND_NAME[kind]} wirklich löschen?${extra}`)) return;
    start(async () => {
      try {
        await deleteEntry(kind, target.id!);
        onDeleted();
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
      }
    });
  };

  // links go to requirement items, not to the Bereiche that group them
  const reqItems = board.requirements.filter((r) => r.parentId !== null);
  const reqOptions = reqItems.map((r) => ({ id: r.id, label: graph.label(key("requirement", r.id)), title: r.title }));
  const mains = board.requirements.filter((r) => r.parentId === null);
  const isMain = kind === "requirement" && form.parentId === null;
  const topics = [...new Set(board.questions.map((q) => q.topic).filter(Boolean))] as string[];

  return (
    <div className="editor-backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <OpenContext.Provider value={openOther}>
      <form className="editor" role="dialog" aria-modal="true" onSubmit={submit}>
        <header>
          <h2>
            {target.id === null
              ? `${KIND_NAME[kind]} anlegen`
              : `${graph.label(key(kind, target.id))} bearbeiten`}
          </h2>
          <button type="button" className="close" onClick={onClose} title="Schließen (Esc)">
            ×
          </button>
        </header>

        <label>
          {kind === "question" ? "Frage" : kind === "measure" ? "Messgröße" : "Titel"}
          <textarea autoFocus rows={2} value={form.title} onChange={(e) => set("title", e.target.value)} />
        </label>

        {(kind === "goal" || kind === "measure") && (
          <label>
            Status <small>Ziele und Messgrößen legt der Kunde fest</small>
            <select value={form.status} onChange={(e) => set("status", e.target.value)}>
              <option value="Entwurf">Entwurf (Vorschlag)</option>
              <option value="abgestimmt">Mit {board.project.client ?? "dem Kunden"} abgestimmt</option>
            </select>
          </label>
        )}

        {kind === "measure" && (
          <label>
            Ziel
            <select
              value={form.goalId ?? ""}
              onChange={(e) => set("goalId", e.target.value ? Number(e.target.value) : null)}
            >
              <option value="">– wählen –</option>
              {board.goals.map((g) => (
                <option key={g.id} value={g.id}>
                  Z{g.num} {g.title}
                </option>
              ))}
            </select>
          </label>
        )}

        {kind === "requirement" && (
          <div className="inline">
            {!isMain && (
              <label>
                Priorität
                <select value={form.priority} onChange={(e) => set("priority", e.target.value)}>
                  <option>Muss</option>
                  <option>Soll</option>
                  <option>Kann</option>
                </select>
              </label>
            )}
            <label className="grow">
              Gehört zu
              <select
                value={form.parentId ?? ""}
                onChange={(e) => set("parentId", e.target.value ? Number(e.target.value) : null)}
              >
                <option value="">– ist selbst ein Bereich –</option>
                {mains
                  .filter((m) => m.id !== target.id)
                  .map((m) => (
                    <option key={m.id} value={m.id}>
                      {graph.label(key("requirement", m.id))} {m.title}
                    </option>
                  ))}
              </select>
            </label>
          </div>
        )}

        {kind === "criterion" && (
          <div className="inline">
            <label className="grow">
              Anforderung
              <select
                value={form.requirementId ?? ""}
                onChange={(e) => set("requirementId", e.target.value ? Number(e.target.value) : null)}
              >
                <option value="">– wählen –</option>
                {mains.map((m) => (
                  <optgroup key={m.id} label={`${graph.label(key("requirement", m.id))} ${m.title}`}>
                    {reqItems
                      .filter((r) => r.parentId === m.id)
                      .map((r) => (
                        <option key={r.id} value={r.id}>
                          {graph.label(key("requirement", r.id))} {r.title}
                        </option>
                      ))}
                  </optgroup>
                ))}
              </select>
            </label>
            <label>
              Status
              <select value={form.status} onChange={(e) => set("status", e.target.value)}>
                <option>offen</option>
                <option>erfüllt</option>
                <option>nicht erfüllt</option>
              </select>
            </label>
          </div>
        )}

        {kind === "feature" && (
          <label>
            Status
            <select value={form.status} onChange={(e) => set("status", e.target.value)}>
              <option>geplant</option>
              <option>in Arbeit</option>
              <option>fertig</option>
            </select>
          </label>
        )}

        {kind === "question" && (
          <>
            <div className="inline">
              <label className="grow">
                Thema
                <input list="topics" value={form.topic} onChange={(e) => set("topic", e.target.value)} />
                <datalist id="topics">
                  {topics.map((t) => (
                    <option key={t} value={t} />
                  ))}
                </datalist>
              </label>
              <label>
                Status
                <select value={form.status} onChange={(e) => set("status", e.target.value)}>
                  <option>offen</option>
                  <option>beantwortet</option>
                </select>
              </label>
            </div>
            <label className="check">
              <input type="checkbox" checked={form.important} onChange={(e) => set("important", e.target.checked)} />
              Für den nächsten Termin wichtig (★)
            </label>
            <label>
              Antwort Michaelshof
              <textarea rows={4} value={form.answer} onChange={(e) => set("answer", e.target.value)} />
            </label>
          </>
        )}

        <label>
          Beschreibung
          <textarea rows={3} value={form.description} onChange={(e) => set("description", e.target.value)} />
        </label>

        {kind === "goal" && (
          <Picker kind="requirement" title="Anforderungen" options={reqOptions} value={form.requirementIds} onChange={(v) => set("requirementIds", v)} />
        )}
        {isMain && target.id !== null && (
          <fieldset className="picker">
            <legend>
              Unteranforderungen <span className="count">{(graph.itemsOfMain.get(target.id) ?? []).length}</span>
            </legend>
            <div className="options">
              {(graph.itemsOfMain.get(target.id) ?? []).map((id) => (
                <div key={id} className="item-link">
                  <span className="id">{graph.label(key("requirement", id))}</span>
                  <EntryLink kind="requirement" id={id}>
                    {graph.title(key("requirement", id))}
                  </EntryLink>
                </div>
              ))}
            </div>
          </fieldset>
        )}
        {kind === "requirement" && !isMain && (
          <>
            <Picker
              kind="goal"
              title="Dient den Zielen"
              options={board.goals.map((g) => ({ id: g.id, label: `Z${g.num}`, title: g.title }))}
              value={form.goalIds}
              onChange={(v) => set("goalIds", v)}
            />
            <Picker
              kind="feature"
              title="Umgesetzt durch Features"
              options={board.features.map((f) => ({ id: f.id, label: `F${f.num}`, title: f.title }))}
              value={form.featureIds}
              onChange={(v) => set("featureIds", v)}
            />
          </>
        )}
        {kind === "feature" && (
          <>
            <Picker kind="requirement" title="Deckt Anforderungen" options={reqOptions} value={form.requirementIds} onChange={(v) => set("requirementIds", v)} />
            <BausteinList value={form.bausteine} onChange={(v) => set("bausteine", v)} />
          </>
        )}
        {kind === "question" ? (
          <LinkPicker board={board} self={target.id} value={form.links} onChange={(v) => set("links", v)} />
        ) : (
          <Picker
            kind="question"
            title="Fragen dazu"
            options={board.questions.map((q) => ({ id: q.id, label: `Q${q.num}`, title: q.title }))}
            value={form.questionIds}
            onChange={(v) => set("questionIds", v)}
          >
            <NewQuestions value={form.newQuestions} onChange={(v) => set("newQuestions", v)} />
          </Picker>
        )}

        {error && <p className="error">{error}</p>}
        <footer>
          <button type="submit" className="primary" disabled={pending}>
            {pending ? "Speichert …" : "Speichern"}
          </button>
          <button type="button" onClick={onClose}>
            Abbrechen
          </button>
          {target.id !== null && (
            <button type="button" className="danger" onClick={remove} disabled={pending}>
              Löschen
            </button>
          )}
        </footer>
      </form>
      </OpenContext.Provider>
    </div>
  );
}

/** Opens another entry from a list in the editor (provided by the Editor). */
const OpenContext = createContext<(kind: Kind, id: number) => void>(() => {});

/** An entry's title in a list: a link that opens that entry. */
function EntryLink({ kind, id, children }: { kind: Kind; id: number; children: React.ReactNode }) {
  const open = useContext(OpenContext);
  return (
    <button
      type="button"
      className="entry-link"
      title="Öffnen"
      onClick={(e) => {
        e.preventDefault();
        open(kind, id);
      }}
    >
      {children}
    </button>
  );
}

function Picker({
  kind,
  title,
  options,
  value,
  onChange,
  children,
}: {
  kind: Kind;
  title: string;
  options: { id: number; label: string; title: string }[];
  value: number[];
  onChange: (v: number[]) => void;
  children?: React.ReactNode;
}) {
  const [q, setQ] = useState("");
  const chosen = new Set(value);
  const s = q.trim().toLowerCase();
  const shown = options.filter((o) => chosen.has(o.id) || !s || `${o.label} ${o.title}`.toLowerCase().includes(s));
  return (
    <fieldset className="picker">
      <legend>
        {title} <span className="count">{value.length}</span>
      </legend>
      <input placeholder="Suchen …" value={q} onChange={(e) => setQ(e.target.value)} />
      <div className="options">
        {shown.map((o) => (
          <label key={o.id} className={chosen.has(o.id) ? "on" : ""}>
            <input
              type="checkbox"
              checked={chosen.has(o.id)}
              onChange={(e) => onChange(e.target.checked ? [...value, o.id] : value.filter((v) => v !== o.id))}
            />
            <span className="id">{o.label}</span>
            <EntryLink kind={kind} id={o.id}>
              {o.title}
            </EntryLink>
          </label>
        ))}
      </div>
      {children}
    </fieldset>
  );
}

function LinkPicker({
  board,
  self,
  value,
  onChange,
}: {
  board: Board;
  self: number | null;
  value: Key[];
  onChange: (v: Key[]) => void;
}) {
  const [q, setQ] = useState("");
  const chosen = new Set(value);
  const all: { k: Key; label: string; title: string }[] = [
    ...board.goals.map((e) => ({ k: key("goal", e.id), label: `Z${e.num}`, title: e.title })),
    ...board.requirements.map((e) => ({ k: key("requirement", e.id), label: `A${e.num}`, title: e.title })),
    ...board.criteria.map((e) => ({ k: key("criterion", e.id), label: `K${e.num}`, title: e.title })),
    ...board.features.map((e) => ({ k: key("feature", e.id), label: `F${e.num}`, title: e.title })),
    ...board.questions
      .filter((e) => e.id !== self)
      .map((e) => ({ k: key("question", e.id), label: `Q${e.num}`, title: e.title })),
  ];
  const s = q.trim().toLowerCase();
  const shown = all.filter((o) => chosen.has(o.k) || !s || `${o.label} ${o.title}`.toLowerCase().includes(s));
  return (
    <fieldset className="picker">
      <legend>
        Betrifft <span className="count">{value.length}</span>
      </legend>
      <input placeholder="Suchen …" value={q} onChange={(e) => setQ(e.target.value)} />
      <div className="options">
        {shown.map((o) => (
          <label key={o.k} className={chosen.has(o.k) ? "on" : ""}>
            <input
              type="checkbox"
              checked={chosen.has(o.k)}
              onChange={(e) => onChange(e.target.checked ? [...value, o.k] : value.filter((v) => v !== o.k))}
            />
            <span className="id">{o.label}</span>
            <EntryLink kind={parseKey(o.k)[0]} id={parseKey(o.k)[1]}>
              {o.title}
            </EntryLink>
          </label>
        ))}
      </div>
    </fieldset>
  );
}

function BausteinList({
  value,
  onChange,
}: {
  value: { id?: number; title: string }[];
  onChange: (v: { id?: number; title: string }[]) => void;
}) {
  const update = (i: number, title: string) => onChange(value.map((b, j) => (j === i ? { ...b, title } : b)));
  const move = (i: number, d: number) => {
    const j = i + d;
    if (j < 0 || j >= value.length) return;
    const next = [...value];
    [next[i], next[j]] = [next[j], next[i]];
    onChange(next);
  };
  return (
    <fieldset className="picker bausteine">
      <legend>
        Bausteine <span className="count">{value.length}</span>
      </legend>
      {value.map((b, i) => (
        <div key={b.id ?? `new-${i}`} className="b">
          <span className="id">{i + 1}</span>
          <input value={b.title} onChange={(e) => update(i, e.target.value)} />
          <button type="button" onClick={() => move(i, -1)} title="Nach oben">
            ↑
          </button>
          <button type="button" onClick={() => move(i, 1)} title="Nach unten">
            ↓
          </button>
          <button type="button" onClick={() => onChange(value.filter((_, j) => j !== i))} title="Entfernen">
            ×
          </button>
        </div>
      ))}
      <button type="button" onClick={() => onChange([...value, { title: "" }])}>
        + Baustein
      </button>
    </fieldset>
  );
}

/** Type a new question and add it; it is created (status offen) and linked when the entry is saved. */
function NewQuestions({ value, onChange }: { value: string[]; onChange: (v: string[]) => void }) {
  const [text, setText] = useState("");
  const add = () => {
    const t = text.trim();
    if (!t) return;
    onChange([...value, t]);
    setText("");
  };
  return (
    <div className="new-questions">
      {value.map((q, i) => (
        <div key={i} className="nq">
          <span className="id">neu</span>
          <span className="grow">{q}</span>
          <button type="button" onClick={() => onChange(value.filter((_, j) => j !== i))} title="Entfernen">
            ×
          </button>
        </div>
      ))}
      <div className="nq-add">
        <input
          placeholder="Neue Frage …"
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              add();
            }
          }}
        />
        <button type="button" onClick={add} disabled={!text.trim()}>
          + Hinzufügen
        </button>
      </div>
    </div>
  );
}

